// PDTC明細(カラー/サイズ/SKU)の派生行生成・サイズ選択列・品名同期をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createPdtcDetailService(deps) {
    const {
        CELLTYPE,
        DeepCopy,
        DicVal,
        GridData,
        PDTC_SIZE_SELECTED_FIELD,
        columninfo,
        columns,
        entrydataRef,
        fnc,
        formstatus,
        getEntryDataValue,
        gridlist,
        initialPdtNameRef,
        isEmpty,
        isEnabled,
        matchesColumnId,
        matchesColumnToken,
        panel,
        setEntryGridRows,
        setSkuNameConfirmState,
        skuNameConfirmState,
        storeRowData
    } = deps;

    function setDetailRowValue(targetRow, columnid, value) {
        if (isEmpty(targetRow) || isEmpty(columnid)) return false;
        const keys = Object.keys(targetRow);
        const exact = keys.find((key) => matchesColumnId(key, columnid));
        const alias = isEmpty(exact) ? keys.find((key) => matchesColumnToken(key, columnid)) : exact;
        const key = isEmpty(alias) ? columnid : alias;
        if (String(DicVal(targetRow, key, "")) === String(value)) return false;
        targetRow[key] = value;
        return true;
    }
    function resolveDetailRowValue(row, columnid, defaultValue = "") {
        if (isEmpty(row) || isEmpty(columnid)) return defaultValue;
        const keys = Object.keys(row);
        const exact = keys.find((key) => matchesColumnId(key, columnid));
        if (!isEmpty(exact)) return DicVal(row, exact, defaultValue);
        const alias = keys.find((key) => matchesColumnToken(key, columnid));
        return isEmpty(alias) ? defaultValue : DicVal(row, alias, defaultValue);
    }
    function pdtcDetailFieldsByType(type) {
        const targetType = String(isEmpty(type) ? "" : type).trim();
        if (targetType === "") return [];
        const source = [
            ...(Array.isArray(columninfo) ? columninfo : []),
            ...(Array.isArray(columns) ? columns : [])
        ];
        if (source.length === 0) return [];
        const used = new Set();
        return source.filter((field) => String(DicVal(field, "type", DicVal(DicVal(field, "colinfo", {}), "type", ""))).trim() === targetType)
            .map((field) => {
                const columnid = String(DicVal(field, "columnid", DicVal(field, "field", ""))).trim();
                if (columnid === "" || used.has(columnid)) return null;
                used.add(columnid);
                return {
                    ...field,
                    columnid: columnid,
                    initvalue: DicVal(field, "initvalue", DicVal(DicVal(field, "colinfo", {}), "initvalue", ""))
                };
            })
            .filter((field) => !isEmpty(field));
    }
    function ensurePdtcDetailRowIds(type, rows) {
        return (Array.isArray(rows) ? rows : []).map((row, index) => ({
            ...(isEmpty(row) ? {} : row),
            rowid: String(DicVal(row, "rowid", `${type}_${index + 1}`))
        }));
    }
    function isPdtcSizeRowSelected(row) {
        const value = DicVal(row, PDTC_SIZE_SELECTED_FIELD, false);
        if (typeof value === "boolean") return value;
        const text = String(isEmpty(value) ? "" : value).trim().toLowerCase();
        return text === "1" || text === "true" || text === "on";
    }
    function pdtcSizeCodesFromSkuRows(skuRows) {
        const codes = new Set();
        (Array.isArray(skuRows) ? skuRows : []).forEach((row) => {
            const sizeCode = String(resolveDetailRowValue(row, "PDTC_SIZECD", "")).trim();
            if (sizeCode !== "") codes.add(sizeCode);
        });
        return codes;
    }
    function hasPdtcSizeSelectionValue(row) {
        return !isEmpty(row) && Object.prototype.hasOwnProperty.call(row, PDTC_SIZE_SELECTED_FIELD);
    }
    function pdtcSizeSelectionValue(row, skuSelected) {
        return isPdtcSizeRowSelected(row) || skuSelected ? "1" : "0";
    }
    function ensurePdtcSizeSelectionDefaults(rows, skuRows = null) {
        const selectedSizeCodes = pdtcSizeCodesFromSkuRows(skuRows);
        return (Array.isArray(rows) ? rows : []).map((row) => {
            const sourceRow = isEmpty(row) ? {} : row;
            const sizeCode = String(resolveDetailRowValue(sourceRow, "PDTC_SIZECD", "")).trim();
            const skuSelected = selectedSizeCodes.size !== 0 && selectedSizeCodes.has(sizeCode);
            return {
                ...sourceRow,
                [PDTC_SIZE_SELECTED_FIELD]: hasPdtcSizeSelectionValue(sourceRow)
                    ? pdtcSizeSelectionValue(sourceRow, false)
                    : pdtcSizeSelectionValue(sourceRow, skuSelected)
            };
        });
    }
    function selectedPdtcSizeRows(rows) {
        return ensurePdtcSizeSelectionDefaults(rows).filter((row) => isPdtcSizeRowSelected(row));
    }
    function canEditPdtcSizeSelectionRow(row) {
        return String(resolveDetailRowValue(row, "PDTC_SIZECD", "")).trim() !== "";
    }
    function buildPdtcSizeSelectionColumns(colDef) {
        const source = (Array.isArray(colDef) ? colDef : [])
            .filter((col) => String(DicVal(col, "field", DicVal(col, "colId", ""))).trim() !== PDTC_SIZE_SELECTED_FIELD);
        const insertIndex = source.findIndex((col) => matchesColumnId(DicVal(col, "field", DicVal(col, "colId", "")), "PDTC_SIZECD"));
        const selectionColumn = {
            field: PDTC_SIZE_SELECTED_FIELD,
            colId: PDTC_SIZE_SELECTED_FIELD,
            headerName: " ",
            width: 36,
            minWidth: 36,
            maxWidth: 36,
            pinned: undefined,
            lockPosition: false,
            editable: (params) => !DicVal(DicVal(params, "context", {}), "entrylock", false)
                && canEditPdtcSizeSelectionRow(DicVal(params, "data", null)),
            filter: false,
            sortable: false,
            resizable: false,
            suppressMenu: true,
            suppressColumnsToolPanel: true,
            colinfo: {
                columnid: PDTC_SIZE_SELECTED_FIELD,
                columnnm: " ",
                datatype: CELLTYPE.CHECK,
                locked: false,
                options: [
                    { code: "1", name: "" },
                    { code: "0", name: "" }
                ]
            }
        };
        const next = [...source];
        next.splice(insertIndex < 0 ? source.length : insertIndex, 0, selectionColumn);
        return next;
    }
    function createDerivedPdtcDetailRow(type) {
        const row = {};
        pdtcDetailFieldsByType(type).forEach((field) => {
            const columnid = String(DicVal(field, "columnid", "")).trim();
            if (columnid === "") return;
            row[columnid] = DicVal(field, "initvalue", "");
        });
        return row;
    }
    function mergeDerivedPdtcDetailRow(targetRow, sourceRow, type) {
        if (isEmpty(targetRow) || isEmpty(sourceRow)) return targetRow;
        pdtcDetailFieldsByType(type).forEach((field) => {
            const columnid = String(DicVal(field, "columnid", "")).trim();
            if (columnid === "") return;
            const value = resolveDetailRowValue(sourceRow, columnid, "");
            if (isEmpty(value)) return;
            targetRow[columnid] = value;
        });
        return targetRow;
    }
    function comparePdtcDerivedDetailRows(left, right, orderColumn, keyColumn) {
        const leftOrder = String(resolveDetailRowValue(left, orderColumn, "")).trim();
        const rightOrder = String(resolveDetailRowValue(right, orderColumn, "")).trim();
        const leftOrderNumber = Number(leftOrder);
        const rightOrderNumber = Number(rightOrder);
        const hasLeftOrderNumber = leftOrder !== "" && Number.isFinite(leftOrderNumber);
        const hasRightOrderNumber = rightOrder !== "" && Number.isFinite(rightOrderNumber);
        if (hasLeftOrderNumber && hasRightOrderNumber && leftOrderNumber !== rightOrderNumber) return leftOrderNumber - rightOrderNumber;
        if (hasLeftOrderNumber !== hasRightOrderNumber) return hasLeftOrderNumber ? -1 : 1;
        const leftKey = String(resolveDetailRowValue(left, keyColumn, "")).trim();
        const rightKey = String(resolveDetailRowValue(right, keyColumn, "")).trim();
        return leftKey.localeCompare(rightKey, "ja", { numeric: true, sensitivity: "base" });
    }
    function buildDerivedPdtcDetails(detailSource, targetType, keyColumn, orderColumn) {
        const skuRows = Array.isArray(DicVal(detailSource, "220", [])) ? DeepCopy(DicVal(detailSource, "220", [])) : [];
        if (skuRows.length === 0) return [];
        const mergedMap = new Map();
        skuRows.forEach((row) => {
            const keyValue = String(resolveDetailRowValue(row, keyColumn, "")).trim();
            if (keyValue === "") return;
            if (!mergedMap.has(keyValue)) {
                const seed = createDerivedPdtcDetailRow(targetType);
                if (!(keyColumn in seed)) seed[keyColumn] = "";
                seed[keyColumn] = keyValue;
                mergedMap.set(keyValue, seed);
            }
            mergeDerivedPdtcDetailRow(mergedMap.get(keyValue), row, targetType);
        });
        return Array.from(mergedMap.values())
            .sort((left, right) => comparePdtcDerivedDetailRows(left, right, orderColumn, keyColumn))
            .map((row, index) => ({
                ...row,
                rowid: String(DicVal(row, "rowid", `${targetType}_${index + 1}`))
            }));
    }
    function buildPdtcSkuDetailKey(colorCode, sizeCode) {
        return `${String(isEmpty(colorCode) ? "" : colorCode).trim()}__${String(isEmpty(sizeCode) ? "" : sizeCode).trim()}`;
    }
    function comparePdtcDetailOrderValue(left, right) {
        const leftText = String(isEmpty(left) ? "" : left).trim();
        const rightText = String(isEmpty(right) ? "" : right).trim();
        const leftNumber = Number(leftText);
        const rightNumber = Number(rightText);
        const hasLeftNumber = leftText !== "" && Number.isFinite(leftNumber);
        const hasRightNumber = rightText !== "" && Number.isFinite(rightNumber);
        if (hasLeftNumber && hasRightNumber && leftNumber !== rightNumber) return leftNumber - rightNumber;
        if (hasLeftNumber !== hasRightNumber) return hasLeftNumber ? -1 : 1;
        return leftText.localeCompare(rightText, "ja", { numeric: true, sensitivity: "base" });
    }
    function buildCombinedPdtcSkuDetails(detailSource) {
        const colorRows = Array.isArray(DicVal(detailSource, "200", [])) ? DeepCopy(DicVal(detailSource, "200", [])) : [];
        const sizeRows = selectedPdtcSizeRows(Array.isArray(DicVal(detailSource, "210", [])) ? DeepCopy(DicVal(detailSource, "210", [])) : []);
        const existingSkuRows = Array.isArray(DicVal(detailSource, "220", [])) ? DeepCopy(DicVal(detailSource, "220", [])) : [];
        if (colorRows.length === 0 || sizeRows.length === 0) return [];

        const existingSkuMap = new Map();
        existingSkuRows.forEach((row) => {
            const colorCode = String(resolveDetailRowValue(row, "PDTC_COLORCD", "")).trim();
            const sizeCode = String(resolveDetailRowValue(row, "PDTC_SIZECD", "")).trim();
            if (colorCode === "" || sizeCode === "") return;
            existingSkuMap.set(buildPdtcSkuDetailKey(colorCode, sizeCode), row);
        });

        const combinedRows = [];
        colorRows.forEach((colorRow) => {
            const colorCode = String(resolveDetailRowValue(colorRow, "PDTC_COLORCD", "")).trim();
            if (colorCode === "") return;
            sizeRows.forEach((sizeRow) => {
                const sizeCode = String(resolveDetailRowValue(sizeRow, "PDTC_SIZECD", "")).trim();
                if (sizeCode === "") return;
                const detailKey = buildPdtcSkuDetailKey(colorCode, sizeCode);
                const row = createDerivedPdtcDetailRow("220");
                mergeDerivedPdtcDetailRow(row, colorRow, "200");
                mergeDerivedPdtcDetailRow(row, sizeRow, "210");
                mergeDerivedPdtcDetailRow(row, existingSkuMap.get(detailKey), "220");
                row.PDTC_COLORCD = colorCode;
                row.PDTC_SIZECD = sizeCode;
                combinedRows.push(row);
            });
        });

        return ensurePdtcDetailRowIds("220", combinedRows.sort((left, right) => {
            const colorCompare = comparePdtcDetailOrderValue(
                resolveDetailRowValue(left, "PDTC_COLORHYOJIJUN", resolveDetailRowValue(left, "PDTC_COLORCD", "")),
                resolveDetailRowValue(right, "PDTC_COLORHYOJIJUN", resolveDetailRowValue(right, "PDTC_COLORCD", ""))
            );
            if (colorCompare !== 0) return colorCompare;
            return comparePdtcDetailOrderValue(
                resolveDetailRowValue(left, "PDTC_SIZEHYOJIJUN", resolveDetailRowValue(left, "PDTC_SIZECD", "")),
                resolveDetailRowValue(right, "PDTC_SIZEHYOJIJUN", resolveDetailRowValue(right, "PDTC_SIZECD", ""))
            );
        }));
    }
    function pdtcDetailPanel(type) {
        const normalizedType = String(type).trim();
        const gridPanel = (Array.isArray(gridlist) ? gridlist : []).find((item) => String(DicVal(item, "type", "")).trim() === normalizedType);
        if (!isEmpty(gridPanel)) return gridPanel;
        return (Array.isArray(panel) ? panel : []).find((item) => String(DicVal(item, "type", "")).trim() === normalizedType) || null;
    }
    function captureInitialPdtName(data) {
        initialPdtNameRef.current = {
            PDT_HINMEINM: String(getEntryDataValue(data, "PDT_HINMEINM", "") ?? ""),
            PDT_HINMEIRN: String(getEntryDataValue(data, "PDT_HINMEIRN", "") ?? "")
        };
    }
    function hasPdtNameChangedFromInitial(data) {
        const initial = DicVal(initialPdtNameRef, "current", {});
        return String(getEntryDataValue(data, "PDT_HINMEINM", "") ?? "") !== String(DicVal(initial, "PDT_HINMEINM", "") ?? "") ||
            String(getEntryDataValue(data, "PDT_HINMEIRN", "") ?? "") !== String(DicVal(initial, "PDT_HINMEIRN", "") ?? "");
    }
    function applyPdtNameToPdtcSkuRows() {
        const skuPanel = pdtcDetailPanel("220");
        if (isEmpty(skuPanel)) return false;
        const currentRows = Array.isArray(GridData(skuPanel)) ? DeepCopy(GridData(skuPanel)) : [];
        if (currentRows.length === 0) return false;
        const hinmeiNm = String(getEntryDataValue(entrydataRef.current, "PDT_HINMEINM", "") ?? "");
        const hinmeiRn = String(getEntryDataValue(entrydataRef.current, "PDT_HINMEIRN", "") ?? "");
        let changed = false;
        const nextRows = currentRows.map((row) => {
            const nextRow = isEmpty(row) ? {} : { ...row };
            changed = setDetailRowValue(nextRow, "PDTC_SKUNAME", hinmeiNm) || changed;
            changed = setDetailRowValue(nextRow, "PDTC_SKURNM", hinmeiRn) || changed;
            return nextRow;
        });
        if (changed) {
            setEntryGridRows(skuPanel, nextRows);
            fnc.current.modify(true);
        }
        return changed;
    }
    function confirmPdtcSkuNameSyncOnSave(onContinue, skipConfirm) {
        if (formstatus !== "ENTRY FORM" || isEnabled(skipConfirm) || !hasPdtNameChangedFromInitial(entrydataRef.current)) {
            if (typeof onContinue === "function") onContinue();
            return;
        }
        storeRowData();
        setSkuNameConfirmState({
            open: true,
            onYes: () => {
                applyPdtNameToPdtcSkuRows();
                if (typeof onContinue === "function") onContinue();
            },
            onNo: () => {
                if (typeof onContinue === "function") onContinue();
            }
        });
    }
    function closeSkuNameConfirmDialog() {
        setSkuNameConfirmState({ open: false, onYes: null, onNo: null });
    }
    function handleSkuNameConfirmYes() {
        const callback = skuNameConfirmState.onYes;
        closeSkuNameConfirmDialog();
        if (typeof callback === "function") callback();
    }
    function handleSkuNameConfirmNo() {
        const callback = skuNameConfirmState.onNo;
        closeSkuNameConfirmDialog();
        if (typeof callback === "function") callback();
    }
    function synchronizePdtcSkuDetails() {
        const colorPanel = pdtcDetailPanel("200");
        const sizePanel = pdtcDetailPanel("210");
        const skuPanel = pdtcDetailPanel("220");
        if (isEmpty(colorPanel) || isEmpty(sizePanel) || isEmpty(skuPanel)) return;
        const detailSource = {
            "200": Array.isArray(GridData(colorPanel)) ? GridData(colorPanel) : [],
            "210": Array.isArray(GridData(sizePanel)) ? GridData(sizePanel) : [],
            "220": Array.isArray(GridData(skuPanel)) ? GridData(skuPanel) : []
        };
        setEntryGridRows(skuPanel, buildCombinedPdtcSkuDetails(detailSource));
    }
    function normalizePdtcDetailSections(detailSource) {
        const next = isEmpty(detailSource) ? {} : DeepCopy(detailSource);
        next["220"] = ensurePdtcDetailRowIds("220", DicVal(next, "220", []));
        const skuRows = Array.isArray(DicVal(next, "220", [])) ? DicVal(next, "220", []) : [];
        if (skuRows.length === 0) return next;
        if (!Array.isArray(next["200"]) || next["200"].length === 0) {
            next["200"] = buildDerivedPdtcDetails(next, "200", "PDTC_COLORCD", "PDTC_COLORHYOJIJUN");
        } else {
            next["200"] = ensurePdtcDetailRowIds("200", next["200"]);
        }
        if (!Array.isArray(next["210"]) || next["210"].length === 0) {
            next["210"] = ensurePdtcSizeSelectionDefaults(buildDerivedPdtcDetails(next, "210", "PDTC_SIZECD", "PDTC_SIZEHYOJIJUN"), skuRows);
        } else {
            next["210"] = ensurePdtcSizeSelectionDefaults(ensurePdtcDetailRowIds("210", next["210"]), skuRows);
        }
        return next;
    }

    return {
        setDetailRowValue,
        resolveDetailRowValue,
        pdtcDetailFieldsByType,
        ensurePdtcDetailRowIds,
        isPdtcSizeRowSelected,
        pdtcSizeCodesFromSkuRows,
        hasPdtcSizeSelectionValue,
        pdtcSizeSelectionValue,
        ensurePdtcSizeSelectionDefaults,
        selectedPdtcSizeRows,
        canEditPdtcSizeSelectionRow,
        buildPdtcSizeSelectionColumns,
        createDerivedPdtcDetailRow,
        mergeDerivedPdtcDetailRow,
        comparePdtcDerivedDetailRows,
        buildDerivedPdtcDetails,
        buildPdtcSkuDetailKey,
        comparePdtcDetailOrderValue,
        buildCombinedPdtcSkuDetails,
        pdtcDetailPanel,
        captureInitialPdtName,
        hasPdtNameChangedFromInitial,
        applyPdtNameToPdtcSkuRows,
        confirmPdtcSkuNameSyncOnSave,
        closeSkuNameConfirmDialog,
        handleSkuNameConfirmYes,
        handleSkuNameConfirmNo,
        synchronizePdtcSkuDetails,
        normalizePdtcDetailSections
    };
}
